import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { WaBriefingTranscribeResponse } from '../types/waBriefing';

/**
 * A voice note for the Morning List page: record from the microphone, send
 * the audio to /wa-briefing/transcribe, hand the text back. Shared by the
 * to-do strip and the cards' "hand it to …" form.
 */
export default function useWaBriefingVoiceNote(params: {
  token: string;
  apiBase: string;
  notify: (message: string) => void;
  onExpired: () => void;
  onText: (text: string) => void;
}) {
  const { token, apiBase, notify, onExpired, onText } = params;
  const { t } = useTranslation();
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const onTextRef = useRef(onText);
  onTextRef.current = onText;

  const stopRecording = () => {
    recorderRef.current?.stop();
    recorderRef.current?.stream.getTracks().forEach((track) => track.stop());
    recorderRef.current = null;
    setRecording(false);
  };

  const transcribe = async (blob: Blob) => {
    setTranscribing(true);
    try {
      const form = new FormData();
      form.append('token', token);
      form.append('audio', blob, 'note.webm');
      const response = await fetch(`${apiBase}/wa-briefing/transcribe`, {
        method: 'POST',
        body: form,
      });
      const payload: WaBriefingTranscribeResponse | null = await response
        .json()
        .catch(() => null);
      if (response.status === 410) {
        onExpired();
        return;
      }
      if (!response.ok || !payload?.data) {
        notify(payload?.error ?? t('briefing.errorBody'));
        return;
      }
      onTextRef.current(payload.data.text.trim());
    } catch {
      notify(t('briefing.errorBody'));
    } finally {
      setTranscribing(false);
    }
  };

  const toggleRecording = async () => {
    if (recording) {
      stopRecording();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, {
          type: recorder.mimeType || 'audio/webm',
        });
        if (blob.size > 0) void transcribe(blob);
      };
      recorder.start();
      recorderRef.current = recorder;
      setRecording(true);
    } catch {
      notify(t('briefing.micUnavailable'));
    }
  };

  useEffect(
    () => () => {
      recorderRef.current?.stream.getTracks().forEach((track) => track.stop());
      recorderRef.current = null;
    },
    [],
  );

  return { recording, transcribing, toggleRecording };
}
